import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBlog } from '../../contexts/BlogContext';
import { 
  FileText, 
  Edit, 
  Trash2, 
  Plus,
  Eye,
  Clock,
  CheckCircle,
  Users,
  Briefcase,
  DollarSign,
  TrendingUp
} from 'lucide-react';

const AdminDashboard = () => {
  const { posts, deletePost } = useBlog();
  const navigate = useNavigate();

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePost(id);
      } catch (err) {
        alert('Error deleting post: ' + err.message);
      }
    }
  };

  // Handle both database format (published: boolean) and old format (status: string)
  const publishedPosts = posts.filter(p => p.published === true || p.status === 'published').length;
  const draftPosts = posts.filter(p => p.published === false || p.status === 'draft').length;

  return (
    <>
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Welcome to Admin Dashboard</h1>
        <p className="text-gray-600">Manage your clients, projects, payments, and blog posts</p>
      </div>

      {/* Removed empty KPI cards to keep dashboard clean */}

      {/* Blog Stats */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-primary mb-4">Blog Management</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Total Posts</p>
                <p className="text-4xl font-bold text-primary mt-2">{posts.length}</p>
              </div>
              <FileText className="w-12 h-12 text-primary opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Published</p>
                <p className="text-4xl font-bold text-green-600 mt-2">{publishedPosts}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Drafts</p>
                <p className="text-4xl font-bold text-orange-600 mt-2">{draftPosts}</p>
              </div>
              <Clock className="w-12 h-12 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mb-6">
          <Link
            to="/admin/posts/new"
            className="bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors font-semibold"
          >
            <Plus className="w-5 h-5" />
            Create New Blog Post
          </Link>
        </div>

        {/* Blog Posts Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary-700">
            <h2 className="text-xl font-bold text-white">All Blog Posts</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No blog posts yet. Create your first post!
                    </td>
                  </tr>
                ) : (
                  posts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-semibold text-gray-900">{post.title}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            {post.excerpt ? `${post.excerpt.substring(0, 80)}...` : 'No excerpt'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                          {/* Handle both database format (category object) and old format (category string) */}
                          {typeof post.category === 'object' && post.category !== null
                            ? post.category.name
                            : post.category || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {/* Handle both database format (published boolean) and old format (status string) */}
                        {post.published === true || post.status === 'published' ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" />
                            Published
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full flex items-center gap-1 w-fit">
                            <Clock className="w-3 h-3" />
                            Draft
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {/* Handle both database format (created_at) and old format (publishedAt) */}
                        {new Date(post.created_at || post.publishedAt || Date.now()).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {(post.published === true || post.status === 'published') && (
                            <Link
                              to={`/blog/${post.slug}`}
                              target="_blank"
                              className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                              title="View Post"
                            >
                              <Eye className="w-5 h-5" />
                            </Link>
                          )}
                          <Link
                            to={`/admin/posts/edit/${post.id}`}
                            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded transition-colors"
                            title="Edit Post"
                          >
                            <Edit className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                            title="Delete Post"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
    </>
  );
};

export default AdminDashboard;
